import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { useStore } from '../state/patientProductionStore';
import { patientApi } from '../api';
import { patientAuthFlow } from '../auth/patientAuthFlow';
import { GOV_TYPE, govPageContainer } from '../ui/PatientUIGovernor';
import { useToast } from '../../../components/ui/Toast';
import type { InsurancePolicy, InsuranceClaim } from '../types';

const STATUS_COLOR: Record<string, string> = {
  SUBMITTED: T.info,
  UNDER_REVIEW: T.warning,
  APPROVED: T.success,
  REJECTED: T.danger,
  SETTLED: T.textSec,
};

const POLICY_STATUS_COLOR: Record<string, string> = {
  ACTIVE: T.success,
  EXPIRED: T.danger,
  LAPSED: T.warning,
  CANCELLED: T.textSec,
};

const dayLabel = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

/** An insurer has confirmed this, as opposed to the patient having typed it. */
const isConfirmed = (p: InsurancePolicy) => p.source === 'INSURER_CONFIRMED' || !!p.verifiedAt;

export default function InsuranceDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'policies' | 'claims'>('policies');
  const [policies, setPolicies] = useState<InsurancePolicy[]>([]);
  const [apiClaims, setApiClaims] = useState<InsuranceClaim[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showAddPolicy, setShowAddPolicy] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const toast = useToast();

  const storeClaims = useStore(s => s.insuranceClaims);
  const authState = patientAuthFlow.getState();
  const nhcId = authState.nhcId || '';

  const loadPolicies = useCallback(() => {
    setLoading(true);
    return patientApi.getInsurancePolicies()
      // Zero policies is a real "you have none yet" state; a failed call
      // is an error state. Neither is a reason to show fabricated data.
      .then((res) => { setPolicies(res); setLoadError(false); })
      .catch(() => { setPolicies([]); setLoadError(true); })
      .finally(() => setLoading(false));
  }, []);

  const loadClaims = useCallback(() => {
    setLoading(true);
    return patientApi.getInsuranceClaims()
      .then((res) => setApiClaims(res))
      .catch(() => setApiClaims([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (tab === 'policies') loadPolicies();
    else loadClaims();
  }, [tab, loadPolicies, loadClaims]);

  // The server's claims are authoritative; a local mirror only fills in for
  // one this device filed but the server list has not come back with.
  const mergedClaims = (() => {
    const apiIds = new Set(apiClaims.map(c => c.id));
    const localOnly = storeClaims.filter(c => !apiIds.has(c.id));
    return [...apiClaims, ...localOnly];
  })();

  const removePolicy = async (p: InsurancePolicy) => {
    if (!window.confirm(`Remove ${p.providerName} ${p.policyNumber} from your account? Claims already filed against it stay on record.`)) return;
    try {
      await patientApi.deleteInsurancePolicy(p.id);
      toast.success('Policy removed');
      await loadPolicies();
    } catch (e: any) {
      toast.error(e?.message || 'Could not remove the policy — please try again.');
    }
  };

  return (
    <div style={{ ...govPageContainer() }}>
      {/* Back nav */}
      <button onClick={() => navigate('/patient/care-services/insurance')} style={{
        display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none',
        fontFamily: T.font, fontSize: 12, fontWeight: 600, color: T.textSec, cursor: 'pointer',
        padding: '12px 0', margin: 0,
      }}>
        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={T.textSec} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
        Insurance & Claims
      </button>

      {/* Header */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ ...GOV_TYPE.pageTitle }}>My Insurance Policies</h1>
          <p style={{ fontSize: 11, color: T.textSec, marginTop: 4 }}>Your policies and claim status</p>
        </div>
        {nhcId && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: `${T.teal}10`, border: `1px solid ${T.teal}25`, borderRadius: 8, padding: '3px 8px', flexShrink: 0 }}>
            <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke={T.teal} strokeWidth="2"><path d="M12 2l8 4v6c0 5-3.5 9.7-8 11-4.5-1.3-8-6-8-11V6l8-4z" /></svg>
            <span style={{ fontSize: 9, fontWeight: 700, color: T.teal }}>{nhcId}</span>
          </div>
        )}
      </div>

      {/* Load error banner — never demo-data substitution */}
      {loadError && (
        <div style={{
          background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 12, padding: '10px 14px',
          marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#EA580C" strokeWidth="2" strokeLinecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
          <span style={{ fontSize: 10, color: '#92400E', lineHeight: 1.5 }}>
            Could not load your policies from the server. Please refresh to try again.
          </span>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, background: T.bg, borderRadius: 12, padding: 4, marginBottom: 16 }}>
        {(['policies', 'claims'] as const).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              flex: 1,
              padding: '8px 16px',
              borderRadius: 10,
              border: 'none',
              fontSize: 13,
              fontWeight: 600,
              fontFamily: T.font,
              cursor: 'pointer',
              background: tab === t ? T.white : 'transparent',
              color: tab === t ? T.text : T.textSec,
              boxShadow: tab === t ? T.shadow : 'none',
              transition: 'all 0.2s',
            }}
          >
            {t === 'policies' ? 'Policies' : 'Claims'}
          </button>
        ))}
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '48px 0', color: T.textSec, fontSize: 12 }}>
          Loading...
        </div>
      )}

      {/* Policies Tab */}
      {!loading && tab === 'policies' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <button
              onClick={() => setShowAddPolicy(true)}
              style={{
                padding: '8px 18px', borderRadius: 10, border: 'none', background: T.teal,
                color: T.white, fontSize: 11, fontWeight: 700, fontFamily: T.font, cursor: 'pointer',
              }}
            >
              + Add Policy
            </button>
          </div>

          {policies.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={T.border} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 12px' }}>
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <p style={{ fontSize: 12, color: T.textSec, margin: '0 0 6px' }}>No insurance policies yet</p>
              <p style={{ fontSize: 11, color: T.textTer, maxWidth: 320, margin: '0 auto 14px', lineHeight: 1.6 }}>
                Add the policy from your insurance card to keep its number, cover and dates to hand — and to file claims against it.
              </p>
              <button
                onClick={() => setShowAddPolicy(true)}
                style={{
                  padding: '9px 20px', borderRadius: 10, border: 'none', background: T.teal,
                  color: T.white, fontSize: 12, fontWeight: 700, fontFamily: T.font, cursor: 'pointer',
                }}
              >
                Add a policy
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {policies.map(policy => {
                // The dates are what decide cover, and the server computes
                // `expired` from them — a status field nobody updated would
                // keep calling a lapsed policy ACTIVE.
                const shownStatus = policy.expired ? 'EXPIRED' : policy.status;
                return (
                <div key={policy.id} style={{
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: 16,
                  padding: 16,
                  boxShadow: T.shadow,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{policy.providerName}</div>
                      <div style={{ fontSize: 11, color: T.textSec, marginTop: 2 }}>
                        {[policy.planName, policy.policyNumber].filter(Boolean).join(' · ')}
                      </div>
                      {policy.memberId && (
                        <div style={{ fontSize: 10, color: T.textTer, marginTop: 2 }}>Member ID {policy.memberId}</div>
                      )}
                    </div>
                    <span style={{
                      fontSize: 9,
                      fontWeight: 600,
                      padding: '3px 8px',
                      borderRadius: 8,
                      background: `${POLICY_STATUS_COLOR[shownStatus] || T.textSec}18`,
                      color: POLICY_STATUS_COLOR[shownStatus] || T.textSec,
                      whiteSpace: 'nowrap',
                    }}>
                      {shownStatus}
                    </span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginTop: 14 }}>
                    <div>
                      <div style={{ fontSize: 9, color: T.textSec }}>Coverage</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginTop: 2 }}>
                        ₹{((policy.coverageAmount || 0) / 100000).toFixed(1)}L
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 9, color: T.textSec }}>Premium</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: T.success, marginTop: 2 }}>
                        {policy.premiumAmount != null ? `₹${policy.premiumAmount.toLocaleString()}` : '—'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 9, color: T.textSec }}>Valid From</div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: T.text, marginTop: 2 }}>
                        {dayLabel(policy.validFrom)}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 9, color: T.textSec }}>Valid Until</div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: T.text, marginTop: 2 }}>
                        {dayLabel(policy.validTo)}
                      </div>
                    </div>
                  </div>

                  {/* Where the policy came from — a badge that says "verified"
                      when nothing verified it would be the claim, not the fact. */}
                  <div style={{ fontSize: 10, color: T.textTer, marginTop: 10, lineHeight: 1.5 }}>
                    {isConfirmed(policy)
                      ? 'Confirmed by your insurer.'
                      : 'You added this policy — NeoHealthCard has not checked it with your insurer.'}
                  </div>

                  {/* Expiring soon — the server's own countdown */}
                  {!policy.expired && policy.daysToExpiry != null && policy.daysToExpiry <= 45 && (
                    <div style={{ marginTop: 10, padding: '8px 12px', background: '#FFFBEB', borderRadius: 10, border: '1px solid #FDE68A' }}>
                      <p style={{ fontFamily: T.font, fontSize: 11, fontWeight: 600, color: '#92400E', margin: 0 }}>
                        {policy.daysToExpiry === 0
                          ? 'Cover ends today.'
                          : `Cover ends in ${policy.daysToExpiry} day${policy.daysToExpiry === 1 ? '' : 's'}.`}
                      </p>
                    </div>
                  )}

                  {/* Expired — renewal happens with the insurer, so point at
                      the insurer's own contact details rather than a button
                      that only opens an alert. */}
                  {policy.expired && (
                    <div style={{ marginTop: 12, padding: '10px 12px', background: '#FEF2F2', borderRadius: 10, border: '1px solid #FCA5A5' }}>
                      <p style={{ fontFamily: T.font, fontSize: 11, fontWeight: 600, color: '#DC2626', margin: '0 0 6px' }}>
                        This policy has expired. Renewal is arranged with your insurer — NeoHealthCard cannot renew it.
                      </p>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {policy.insurerPhone && (
                          <a href={`tel:${policy.insurerPhone}`} style={{
                            fontFamily: T.font, fontSize: 11, fontWeight: 700, color: T.white,
                            background: '#DC2626', borderRadius: 8, padding: '7px 16px', textDecoration: 'none',
                          }}>Call {policy.insurerPhone}</a>
                        )}
                        {policy.insurerWebsite && (
                          <a href={policy.insurerWebsite} target="_blank" rel="noreferrer" style={{
                            fontFamily: T.font, fontSize: 11, fontWeight: 700, color: '#DC2626',
                            background: T.white, border: '1px solid #FCA5A5', borderRadius: 8, padding: '6px 16px', textDecoration: 'none',
                          }}>Insurer website</a>
                        )}
                        {!policy.insurerPhone && !policy.insurerWebsite && (
                          <span style={{ fontFamily: T.font, fontSize: 10, color: '#92400E' }}>
                            No insurer contact recorded on this policy.
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Policy actions */}
                  <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                    <button onClick={() => {
                      const text = `Policy: ${policy.policyNumber}\nInsurer: ${policy.providerName}\nPlan: ${policy.planName ?? '—'}\nCoverage: ₹${(policy.coverageAmount || 0).toLocaleString()}\nValid: ${dayLabel(policy.validFrom)} to ${dayLabel(policy.validTo)}\nStatus: ${shownStatus}`;
                      if (navigator.share) navigator.share({ title: 'Insurance Policy', text });
                      else { navigator.clipboard.writeText(text); toast.success('Policy details copied to clipboard'); }
                    }} style={{
                      fontFamily: T.font, fontSize: 10, fontWeight: 600, color: T.info, background: `${T.info}10`,
                      border: `1px solid ${T.info}30`, borderRadius: 8, padding: '5px 10px', cursor: 'pointer',
                    }}>
                      Share Policy
                    </button>
                    <button onClick={() => window.print()} style={{
                      fontFamily: T.font, fontSize: 10, fontWeight: 600, color: T.textSec, background: T.bg,
                      border: `1px solid ${T.border}`, borderRadius: 8, padding: '5px 10px', cursor: 'pointer',
                    }}>
                      Download Card
                    </button>
                    {!policy.expired && (
                      <button onClick={() => { setTab('claims'); setShowModal(true); }} style={{
                        fontFamily: T.font, fontSize: 10, fontWeight: 700, color: T.white, background: T.teal,
                        border: 'none', borderRadius: 8, padding: '6px 12px', cursor: 'pointer',
                      }}>
                        File a Claim
                      </button>
                    )}
                    <button onClick={() => removePolicy(policy)} style={{
                      marginLeft: 'auto', fontFamily: T.font, fontSize: 10, fontWeight: 600, color: T.textTer,
                      background: 'transparent', border: 'none', cursor: 'pointer', padding: '5px 4px',
                    }}>
                      Remove
                    </button>
                  </div>

                </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Claims Tab */}
      {!loading && tab === 'claims' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <button
              onClick={() => setShowModal(true)}
              style={{
                padding: '8px 18px',
                borderRadius: 10,
                border: 'none',
                background: T.info,
                color: T.white,
                fontSize: 11,
                fontWeight: 700,
                fontFamily: T.font,
                cursor: 'pointer',
              }}
            >
              + File New Claim
            </button>
          </div>

          {mergedClaims.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={T.border} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 12px' }}>
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
              <p style={{ fontSize: 12, color: T.textSec }}>No insurance claims found</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {mergedClaims.map((claim: any) => (
                <div key={claim.id} style={{
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: 16,
                  padding: 16,
                  boxShadow: T.shadow,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>
                        {claim.serviceName || claim.description || claim.serviceType || 'Claim'}
                      </div>
                      <div style={{ fontSize: 11, color: T.textSec, marginTop: 2 }}>
                        {/* The claim number is the reference an insurer can
                            look up; the policy number says which cover it is
                            against. Both, not one standing in for the other. */}
                        {[claim.claimNumber, claim.policyNumber && `Policy ${claim.policyNumber}`].filter(Boolean).join(' · ')}
                      </div>
                      {(claim.hospitalName || claim.hospital) && (
                        <div style={{ fontSize: 10, color: T.textTer, marginTop: 2 }}>{claim.hospitalName || claim.hospital}</div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <span style={{
                        fontSize: 9,
                        fontWeight: 600,
                        padding: '3px 8px',
                        borderRadius: 8,
                        background: `${T.info}18`,
                        color: T.info,
                        whiteSpace: 'nowrap',
                      }}>
                        {claim.type === 'Preauth' ? 'Pre-auth' : 'Claim'}
                      </span>
                      <span style={{
                        fontSize: 9,
                        fontWeight: 600,
                        padding: '3px 8px',
                        borderRadius: 8,
                        background: `${STATUS_COLOR[claim.status] || T.textSec}18`,
                        color: STATUS_COLOR[claim.status] || T.textSec,
                        whiteSpace: 'nowrap',
                      }}>
                        {(claim.status || '').replace(/_/g, ' ')}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginTop: 14 }}>
                    <div>
                      <div style={{ fontSize: 9, color: T.textSec }}>Amount Claimed</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginTop: 2 }}>
                        ₹{(claim.amount || 0).toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 9, color: T.textSec }}>Date</div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: T.text, marginTop: 2 }}>
                        {dayLabel(claim.claimDate || claim.createdAt)}
                      </div>
                    </div>
                  </div>

                  {claim.settledAmount != null && (
                    <div style={{ marginTop: 10, fontSize: 11, fontWeight: 700, color: T.success }}>
                      Settled ₹{claim.settledAmount.toLocaleString()}
                    </div>
                  )}
                  {claim.rejectionReason && (
                    <div style={{ marginTop: 10, fontSize: 11, color: T.danger, lineHeight: 1.5 }}>
                      Reason: {claim.rejectionReason}
                    </div>
                  )}
                  {claim.insurerReference && (
                    <div style={{ marginTop: 8, fontSize: 10, color: T.textTer }}>
                      Insurer reference {claim.insurerReference}
                    </div>
                  )}
                  {Array.isArray(claim.documents) && claim.documents.length > 0 && (
                    <div style={{ marginTop: 8, fontSize: 10, color: T.textTer, lineHeight: 1.6 }}>
                      Documents named: {claim.documents.map((d: string) => d.replace(/\s*\(vault:[^)]*\)$/, '')).join(', ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Compliance Note */}
      <div style={{
        marginTop: 20,
        padding: 12,
        borderRadius: 12,
        background: '#0891B208',
        border: '1px solid #0891B215',
        fontSize: 9,
        color: T.textSec,
        lineHeight: 1.7,
        fontFamily: T.font,
      }}>
        Insurance approval is subject to insurer verification. NeoHealthCard stores and shares consented health/claim documents; it does not guarantee claim approval. Final approval, rejection, co-pay, and settlement are decided by the insurer and hospital. Policy information shown here is informational until verified by your insurer.
      </div>

      {/* File New Claim Modal */}
      {showModal && (
        <ClaimModal
          policies={policies}
          onClose={() => setShowModal(false)}
          onFiled={() => { setShowModal(false); loadClaims(); }}
        />
      )}

      {/* Add Policy Modal */}
      {showAddPolicy && (
        <AddPolicyModal
          onClose={() => setShowAddPolicy(false)}
          onAdded={() => { setShowAddPolicy(false); loadPolicies(); }}
        />
      )}
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, color: T.text, fontFamily: T.font,
  marginBottom: 4, display: 'block',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', borderRadius: 10, border: `1px solid ${T.border}`,
  fontSize: 12, fontFamily: T.font, color: T.text, background: T.white,
  outline: 'none', boxSizing: 'border-box',
};

function ModalShell({ title, onClose, children, footer }: {
  title: string; onClose: () => void; children: React.ReactNode; footer: React.ReactNode;
}) {
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: 9999, padding: 16,
    }}>
      <div style={{
        background: T.white, borderRadius: 16, padding: 24, width: '100%', maxWidth: 420,
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.15)', fontFamily: T.font,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: T.text, margin: 0 }}>{title}</h2>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', fontSize: 18, color: T.textSec,
            cursor: 'pointer', padding: 4, lineHeight: 1,
          }}>×</button>
        </div>
        {children}
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>{footer}</div>
      </div>
    </div>
  );
}

const cancelBtn: React.CSSProperties = {
  flex: 1, padding: '10px 0', borderRadius: 10, border: `1px solid ${T.border}`,
  background: T.white, color: T.textSec, fontSize: 12, fontWeight: 600,
  fontFamily: T.font, cursor: 'pointer',
};

const submitBtn = (enabled: boolean, busy: boolean, bg: string): React.CSSProperties => ({
  flex: 1, padding: '10px 0', borderRadius: 10, border: 'none',
  background: enabled ? bg : T.border, color: T.white, fontSize: 12, fontWeight: 700,
  fontFamily: T.font, cursor: enabled ? 'pointer' : 'not-allowed', opacity: busy ? 0.6 : 1,
});

/**
 * Records a policy the patient holds (`POST /insurance`).
 *
 * Without this, a patient with no policy could never file a claim: the server
 * checks the claim's policy number against their own policies and refuses
 * anything else.
 */
function AddPolicyModal({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const toast = useToast();
  const [form, setForm] = useState({
    providerName: '', planName: '', policyNumber: '', memberId: '',
    coverageAmount: '', premiumAmount: '', insurerPhone: '', validFrom: '', validTo: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (key: keyof typeof form, val: string) => setForm(f => ({ ...f, [key]: val }));

  const ready = !!form.providerName.trim() && !!form.policyNumber.trim()
    && Number(form.coverageAmount) > 0 && !!form.validFrom && !!form.validTo;

  const handleSubmit = async () => {
    if (!ready) return;
    // The server refuses this too; checking here says which field is wrong.
    if (new Date(form.validTo) <= new Date(form.validFrom)) {
      toast.error('The policy must end after it starts.');
      return;
    }
    setSubmitting(true);
    try {
      await patientApi.createInsurancePolicy({
        providerName: form.providerName.trim(),
        policyNumber: form.policyNumber.trim(),
        coverageAmount: Number(form.coverageAmount),
        validFrom: form.validFrom,
        validTo: form.validTo,
        planName: form.planName.trim() || undefined,
        memberId: form.memberId.trim() || undefined,
        premiumAmount: form.premiumAmount ? Number(form.premiumAmount) : undefined,
        insurerPhone: form.insurerPhone.trim() || undefined,
      });
      toast.success('Policy added');
      onAdded();
    } catch (e: any) {
      // "That policy number is already on your account" says more than a
      // generic failure ever could.
      toast.error(e?.message || 'Could not add the policy — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="Add a policy"
      onClose={onClose}
      footer={<>
        <button onClick={onClose} style={cancelBtn}>Cancel</button>
        <button onClick={handleSubmit} disabled={submitting || !ready} style={submitBtn(ready, submitting, T.teal)}>
          {submitting ? 'Saving...' : 'Save Policy'}
        </button>
      </>}
    >
      <div style={{ background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 10, padding: '8px 12px', marginBottom: 14 }}>
        <p style={{ fontFamily: T.font, fontSize: 10, color: '#075985', margin: 0, lineHeight: 1.5 }}>
          Enter the policy exactly as it appears on your insurance card. It is recorded as self-reported — NeoHealthCard does not check it with your insurer.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <label style={labelStyle} htmlFor="ins-provider">Insurer *</label>
          <input id="ins-provider" style={inputStyle} placeholder="e.g. Star Health"
            value={form.providerName} onChange={e => set('providerName', e.target.value)} />
        </div>
        <div>
          <label style={labelStyle} htmlFor="ins-plan">Plan</label>
          <input id="ins-plan" style={inputStyle} placeholder="e.g. Comprehensive Individual"
            value={form.planName} onChange={e => set('planName', e.target.value)} />
        </div>
        <div>
          <label style={labelStyle} htmlFor="ins-number">Policy number *</label>
          <input id="ins-number" style={inputStyle} placeholder="As printed on your card"
            value={form.policyNumber} onChange={e => set('policyNumber', e.target.value)} />
        </div>
        <div>
          <label style={labelStyle} htmlFor="ins-member">Member ID</label>
          <input id="ins-member" style={inputStyle} placeholder="If your card shows one"
            value={form.memberId} onChange={e => set('memberId', e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle} htmlFor="ins-cover">Sum insured (₹) *</label>
            <input id="ins-cover" style={inputStyle} type="number" placeholder="500000"
              value={form.coverageAmount} onChange={e => set('coverageAmount', e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle} htmlFor="ins-premium">Annual premium (₹)</label>
            <input id="ins-premium" style={inputStyle} type="number" placeholder="14200"
              value={form.premiumAmount} onChange={e => set('premiumAmount', e.target.value)} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle} htmlFor="ins-from">Valid from *</label>
            <input id="ins-from" style={inputStyle} type="date"
              value={form.validFrom} onChange={e => set('validFrom', e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle} htmlFor="ins-to">Valid until *</label>
            <input id="ins-to" style={inputStyle} type="date"
              value={form.validTo} onChange={e => set('validTo', e.target.value)} />
          </div>
        </div>
        <div>
          <label style={labelStyle} htmlFor="ins-phone">Insurer helpline</label>
          <input id="ins-phone" style={inputStyle} placeholder="The number on your card"
            value={form.insurerPhone} onChange={e => set('insurerPhone', e.target.value)} />
        </div>
      </div>
    </ModalShell>
  );
}

function ClaimModal({ policies, onClose, onFiled }: {
  policies: InsurancePolicy[]; onClose: () => void; onFiled: () => void;
}) {
  const toast = useToast();
  // Only a policy still in cover — the server refuses an expired one.
  const claimable = policies.filter(p => !p.expired && p.policyNumber);
  const [form, setForm] = useState({
    policyNumber: claimable[0]?.policyNumber ?? '',
    type: 'Claim' as 'Claim' | 'Preauth',
    amount: '',
    description: '',
    serviceName: '',
    hospitalName: '',
    documents: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (key: keyof typeof form, val: string) => setForm(f => ({ ...f, [key]: val }));
  const ready = !!form.policyNumber && !!form.amount && !!form.description;

  const handleSubmit = async () => {
    if (!ready) return;
    setSubmitting(true);
    try {
      const created = await patientApi.createInsuranceClaim({
        policyNumber: form.policyNumber,
        type: form.type,
        amount: Number(form.amount),
        description: form.description,
        // Own fields on the schema — not folded into the description.
        serviceName: form.serviceName.trim() || undefined,
        hospitalName: form.hospitalName.trim() || undefined,
        documents: form.documents ? [form.documents] : [],
      });
      toast.success(`${form.type === 'Preauth' ? 'Pre-auth' : 'Claim'} recorded — reference ${created.claimNumber}`);
      onFiled();
    } catch (e: any) {
      // A claim the server never accepted must not be stored/shown as
      // SUBMITTED — surface the failure and let the user retry.
      toast.error(e?.message || 'Submission failed — the server is unreachable. Your claim was NOT filed; please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (claimable.length === 0) {
    return (
      <ModalShell
        title="File New Claim"
        onClose={onClose}
        footer={<button onClick={onClose} style={cancelBtn}>Close</button>}
      >
        <p style={{ fontFamily: T.font, fontSize: 12, color: T.textSec, margin: 0, lineHeight: 1.6 }}>
          {policies.length === 0
            ? 'A claim is filed against one of your policies, and you have none on your account yet. Add a policy on the Policies tab first.'
            : 'Every policy on your account has expired. Your insurer will not accept a claim against an expired policy.'}
        </p>
      </ModalShell>
    );
  }

  return (
    <ModalShell
      title="File New Claim"
      onClose={onClose}
      footer={<>
        <button onClick={onClose} style={cancelBtn}>Cancel</button>
        <button onClick={handleSubmit} disabled={submitting || !ready} style={submitBtn(ready, submitting, T.info)}>
          {submitting ? 'Submitting...' : `Submit ${form.type === 'Preauth' ? 'Pre-auth' : 'Claim'}`}
        </button>
      </>}
    >
      <div style={{ background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 10, padding: '8px 12px', marginBottom: 14 }}>
        <p style={{ fontFamily: T.font, fontSize: 10, color: '#92400E', margin: 0, lineHeight: 1.5 }}>
          This files a real record on your NeoHealthCard account. There is no direct insurer integration — quote the reference to your insurer/TPA, who decide approval and settlement.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <label style={labelStyle} htmlFor="clm-policy">Policy</label>
          <select id="clm-policy" style={{ ...inputStyle, appearance: 'auto' as any }}
            value={form.policyNumber} onChange={e => set('policyNumber', e.target.value)}>
            {claimable.map(p => (
              <option key={p.id} value={p.policyNumber}>
                {[p.providerName, p.planName].filter(Boolean).join(' · ')} — {p.policyNumber}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={labelStyle} htmlFor="clm-type">Type</label>
          <select id="clm-type" style={{ ...inputStyle, appearance: 'auto' as any }}
            value={form.type} onChange={e => set('type', e.target.value)}>
            <option value="Claim">Claim — treatment already received</option>
            <option value="Preauth">Pre-authorization — before treatment</option>
          </select>
        </div>

        <div>
          <label style={labelStyle} htmlFor="clm-amount">Amount (₹)</label>
          <input id="clm-amount" style={inputStyle} type="number" placeholder="0"
            value={form.amount} onChange={e => set('amount', e.target.value)} />
        </div>

        <div>
          <label style={labelStyle} htmlFor="clm-hospital">Hospital / Provider</label>
          <input id="clm-hospital" style={inputStyle} placeholder="Where treatment was received"
            value={form.hospitalName} onChange={e => set('hospitalName', e.target.value)} />
        </div>

        <div>
          <label style={labelStyle} htmlFor="clm-service">Service / Procedure</label>
          <input id="clm-service" style={inputStyle} placeholder="e.g. Knee replacement surgery"
            value={form.serviceName} onChange={e => set('serviceName', e.target.value)} />
        </div>

        <div>
          <label style={labelStyle} htmlFor="clm-desc">Description</label>
          <textarea id="clm-desc" style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }}
            placeholder="Diagnosis, treatment, dates..."
            value={form.description} onChange={e => set('description', e.target.value)} />
        </div>

        <div>
          <label style={labelStyle} htmlFor="clm-doc">Document reference (optional)</label>
          <input id="clm-doc" style={inputStyle}
            placeholder="e.g. Hospital bill no. / Health Vault document title"
            value={form.documents} onChange={e => set('documents', e.target.value)} />
          <div style={{ fontSize: 9, color: T.textTer, fontFamily: T.font, marginTop: 3 }}>
            Upload files in Health Vault, then reference them here — the reference is saved on the claim.
          </div>
        </div>
      </div>
    </ModalShell>
  );
}
